import { useCallback, useMemo } from 'react';

import type { AnalyticsEvents } from '@sunflower-games/analytics-events';

import type { WizardChildProps } from '../../../components/Wizard/types';
import { useMissedWelcomeBonus } from '../../../hooks/useMissedWelcomeBonus';
import { analytics } from '../../../services/analytics';
import { auth, type AuthParams } from '../../../services/auth';
import { useStores } from '../../../stores/useStores';
import { useHandleAuth } from '../useHandleAuth';

import { SignupWizardOrderRedesignFlow, SignUpWizardStepsRedesign } from './constants';
import { useSignUpWizardContext } from './signUpWizard.provider';

type Params =
  | (Partial<AuthParams> & {
      componentType?: 'dialog' | 'portal';
      areAllOptionalConsentsChecked?: boolean;
      wizardProps?: Partial<WizardChildProps>;
    })
  | null
  | undefined;

export const useHandleSignUpWizardRedesign = () => {
  const { authStore, wizardStore } = useStores();

  const { step, nextStep } = wizardStore;

  const { updateContextData, provider } = useSignUpWizardContext();
  const { setMissedWelcomeBonusUserData } = useMissedWelcomeBonus();

  const { handleAuth } = useHandleAuth((error) => {
    updateContextData({ error });
  }, nextStep);

  const isDirectEmailLanding = authStore.isDirectEmailLanding();
  const isDirectRegLanding = authStore.isDirectRegLanding();

  const signUpProvider = provider || auth.getAuthParams()?.provider;

  const signUpWizardStep = useMemo(() => {
    return SignupWizardOrderRedesignFlow[step];
  }, [step]);

  const handleSignUpAndEvents = useCallback(
    async (params: Params) => {
      const eventParams: AnalyticsEvents['create_account'] = {
        event_action: 'click',
        event_category: 'sign_up',
        event_type: 'signup_platform',
        event_value: params?.provider || 'email',
      };
      void analytics.sendEvent('create_account', eventParams);

      if (params?.provider && params.provider === 'email') {
        updateContextData({ loading: true });
      }
      try {
        return await handleAuth({
          provider: params?.provider || 'email',
          action: 'sign_up',
          credentials: params?.credentials,
          nextStep: params?.wizardProps?.nextStep || nextStep,
          ...params?.wizardProps,
        });
      } finally {
        updateContextData({ loading: false });
      }
    },
    [handleAuth, nextStep, updateContextData],
  );

  const handleRegularLanding = useCallback(
    (params: Params) => {
      switch (signUpWizardStep) {
        case SignUpWizardStepsRedesign.SignUpOptions: {
          const providerValue = params?.provider || signUpProvider;
          if (!providerValue) {
            throw new Error('Provider is not set');
          }

          if (providerValue === 'email') {
            const { email, password } = params?.credentials || {};
            if (!email || !password) {
              return;
            }
          }

          void handleSignUpAndEvents({
            provider: providerValue,
            action: 'sign_up',
            credentials: params?.credentials,
          }).then(({ user }) => {
            if (user) {
              setMissedWelcomeBonusUserData({ userId: user.uid });
            }
          });
          break;
        }
        case SignUpWizardStepsRedesign.EmailVerification:
        case SignUpWizardStepsRedesign.ResponsibleSocialPlay:
        case SignUpWizardStepsRedesign.ChooseCountry: {
          nextStep();
          break;
        }
      }
    },
    [handleSignUpAndEvents, nextStep, setMissedWelcomeBonusUserData, signUpProvider, signUpWizardStep],
  );

  const handleRegLanding = useCallback(
    (params: Params) => {
      void handleSignUpAndEvents({
        provider: params?.provider || 'email',
        action: 'sign_up',
        credentials: params?.credentials,
        wizardProps: params?.wizardProps,
      });
    },
    [handleSignUpAndEvents],
  );

  const handleSignUpWizard = useCallback(
    (params?: Params | null) => {
      if (params?.provider) {
        updateContextData({
          provider: params.provider,
          credentials: params?.credentials,
        });
      }
      if (isDirectRegLanding) {
        return handleRegLanding(params);
      }
      return handleRegularLanding(params);
    },
    [isDirectRegLanding, handleRegularLanding, updateContextData, handleRegLanding],
  );

  return { handleSignUpWizard, signUpWizardStep, isDirectEmailLanding };
};
